/**
 * readerAssignment.js — Portal-side reader assignment helpers.
 *
 * These functions write to Firestore from the web portal (no Stripe Terminal SDK,
 * no AsyncStorage). The POS app's TerminalContext subscribes to device docs in
 * real-time and reacts to assignment changes immediately.
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Assign a reader to a specific device.
 * Also clears that reader from any other device that currently holds it,
 * so there are never two tablets pointing at the same reader.
 *
 * @param {string} restaurantId
 * @param {string} targetDeviceId   — device to receive the reader
 * @param {object} reader           — { serialNumber, deviceType, label? }
 * @param {Array}  allDevices       — current device list to find and clear conflicts
 */
export async function assignReaderToDevice(restaurantId, targetDeviceId, reader, allDevices = []) {
  const readerInfo = {
    serialNumber: reader.serialNumber,
    deviceType:   reader.deviceType || null,
    label:        reader.label      || null,
    assignedAt:   new Date().toISOString(),
    assignedBy:   'portal',
  };

  // Clear this reader from any other device that currently holds it
  const conflicts = allDevices.filter(
    d => d.id !== targetDeviceId && d.assignedReader?.serialNumber === reader.serialNumber,
  );
  await Promise.all(
    conflicts.map(d =>
      updateDoc(doc(db, 'restaurants_data', restaurantId, 'devices', d.id), { assignedReader: null }),
    ),
  );

  // Assign to target device
  await updateDoc(
    doc(db, 'restaurants_data', restaurantId, 'devices', targetDeviceId),
    { assignedReader: readerInfo },
  );
}

/**
 * Remove the reader assignment from a device.
 * The tablet's TerminalContext will detect this change and stop reconnecting.
 */
export async function clearReaderAssignment(restaurantId, deviceId) {
  await updateDoc(
    doc(db, 'restaurants_data', restaurantId, 'devices', deviceId),
    { assignedReader: null },
  );
}
